import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  selector: 'app-copy-cell-renderer',
  template: `
    <div class="copy-cell-container">
      <span class="cell-value" [title]="getValue()">{{ getValue() }}</span>
      <button
        *ngIf="getValue() && getValue().trim().length > 0"
        class="copy-btn"
        (click)="copyToClipboard($event)"
        [title]="'Copy ' + getValue()"
        type="button"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor" stroke-width="2" fill="none"/>
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      </button>
    </div>
  `,
  styles: [`
    .copy-cell-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      height: 100%;
      gap: 4px;
    }

    .cell-value {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 14px;
      line-height: 1.2;
      width:100px;
    }

    .copy-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: none;
      border: none;
      cursor: pointer;
      padding: 2px;
      border-radius: 3px;
      color: #6b7280;
      transition: all 0.2s ease;
      flex-shrink: 0;
      width: 20px;
      height: 20px;
    }

    .copy-btn:hover {
      background-color: #f3f4f6;
      color: #374151;
    }

    .copy-btn:active {
      background-color: #e5e7eb;
      transform: scale(0.95);
    }

    .copy-btn svg {
      width: 14px;
      height: 14px;
    }

    /* Hide copy button on mobile to save space */
    @media (max-width: 768px) {
      .copy-btn {
        display: none;
      }
    }
  `]
})
export class CopyCellRendererComponent implements ICellRendererAngularComp {
  private params!: ICellRendererParams;

  agInit(params: ICellRendererParams): void {
    this.params = params;
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    return true;
  }

  getValue(): string {
    if (!this.params?.value) return '';
    return String(this.params.value).trim();
  }

  async copyToClipboard(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const value = this.getValue();
    if (!value) return;

    try {
      if (navigator.clipboard && window.isSecureContext) {
        // Modern clipboard API
        await navigator.clipboard.writeText(value);
        console.log(`📋 Copied to clipboard: ${value}`);
        this.showCopyFeedback();
      } else {
        // Fallback for older browsers or non-HTTPS
        this.fallbackCopyToClipboard(value);
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Try fallback method
      this.fallbackCopyToClipboard(value);
    }
  }

  private fallbackCopyToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      console.log(`📋 Copied to clipboard (fallback): ${text}`);
      this.showCopyFeedback();
    } catch (error) {
      console.error('Fallback copy failed:', error);
    } finally {
      document.body.removeChild(textArea);
    }
  }

  private showCopyFeedback(): void {
    // Create and show a temporary toast notification
    const toastElement = document.createElement('div');
    toastElement.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #10B981;
        color: white;
        padding: 12px 16px;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        z-index: 10000;
        transform: translateX(100%);
        transition: transform 0.3s ease-in-out;
      ">
        <div style="display: flex; align-items: center; gap: 8px;">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Copied!
        </div>
      </div>
    `;

    document.body.appendChild(toastElement);

    // Animate in
    setTimeout(() => {
      const toast = toastElement.firstElementChild as HTMLElement;
      if (toast) {
        toast.style.transform = 'translateX(0)';
      }
    }, 10);

    // Animate out and remove
    setTimeout(() => {
      const toast = toastElement.firstElementChild as HTMLElement;
      if (toast) {
        toast.style.transform = 'translateX(100%)';
      }
      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement);
        }
      }, 300);
    }, 2000);

    // Also update button appearance briefly
    const button = document.activeElement as HTMLButtonElement;
    if (button && button.classList.contains('copy-btn')) {
      const originalTitle = button.title;
      button.title = 'Copied!';
      button.style.color = '#10B981';

      setTimeout(() => {
        button.title = originalTitle;
        button.style.color = '';
      }, 1000);
    }
  }
}
